import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useState, useEffect, useRef } from 'react';

interface RiskNodeInput {
  id: string;
  name: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

interface RiskNode extends RiskNodeInput {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

interface RiskDependency {
  source: string;
  target: string;
  type: 'triggers' | 'mitigates' | 'related';
}

interface RiskDependencyMapProps {
  nodes: RiskNodeInput[];
  dependencies: RiskDependency[];
  className?: string;
}

const getSeverityColor = (severity: string): string => {
  switch (severity) {
    case 'critical':
      return 'bg-red-600 border-red-500';
    case 'high':
      return 'bg-orange-600 border-orange-500';
    case 'medium':
      return 'bg-yellow-600 border-yellow-500';
    case 'low':
      return 'bg-green-700 border-green-600';
    default:
      return 'bg-neutral-700 border-neutral-600';
  }
};

const getDependencyColor = (type: string): string => {
  switch (type) {
    case 'triggers':
      return 'stroke-red-500';
    case 'mitigates':
      return 'stroke-green-500';
    case 'related':
      return 'stroke-cyan-500';
    default:
      return 'stroke-neutral-600';
  }
};

export function RiskDependencyMap({ nodes: initialNodes, dependencies, className }: RiskDependencyMapProps) {
  const [nodes, setNodes] = useState<RiskNode[]>([]);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number>();

  // Simple force-directed layout simulation
  useEffect(() => {
    const width = 600;
    const height = 400;
    const centerX = width / 2;
    const centerY = height / 2;

    // Initialize positions in a circle
    const nodesWithPositions = initialNodes.map((node, idx) => {
      const angle = (idx / initialNodes.length) * 2 * Math.PI;
      const radius = Math.min(width, height) * 0.35;
      return {
        ...node,
        x: centerX + Math.cos(angle) * radius,
        y: centerY + Math.sin(angle) * radius,
        vx: 0,
        vy: 0,
      };
    });

    setNodes(nodesWithPositions);

    // Simple force simulation
    let iterations = 0;
    const maxIterations = 100;

    const simulate = () => {
      if (iterations >= maxIterations) return;

      setNodes(prevNodes => {
        const newNodes: RiskNode[] = prevNodes.map(node => ({
          ...node,
          x: node.x,
          y: node.y,
          vx: node.vx,
          vy: node.vy,
        }));

        // Repulsion between nodes
        // @ts-ignore - Array access is safe here
        for (let i = 0; i < newNodes.length; i++) {
          for (let j = i + 1; j < newNodes.length; j++) {
            const nodeI = newNodes[i];
            const nodeJ = newNodes[j];
            if (!nodeI || !nodeJ) continue;
            
            const dx = nodeJ.x - nodeI.x;
            const dy = nodeJ.y - nodeI.y;
            const distance = Math.sqrt(dx * dx + dy * dy) || 1;
            const force = 1000 / (distance * distance);

            nodeI.vx -= (dx / distance) * force;
            nodeI.vy -= (dy / distance) * force;
            nodeJ.vx += (dx / distance) * force;
            nodeJ.vy += (dy / distance) * force;
          }
        }

        // Attraction along dependencies
        dependencies.forEach(dep => {
          const source = newNodes.find(n => n.id === dep.source);
          const target = newNodes.find(n => n.id === dep.target);
          if (source && target) {
            const dx = target.x - source.x;
            const dy = target.y - source.y;
            const distance = Math.sqrt(dx * dx + dy * dy) || 1;
            const force = distance * 0.01;

            source.vx += (dx / distance) * force;
            source.vy += (dy / distance) * force;
            target.vx -= (dx / distance) * force;
            target.vy -= (dy / distance) * force;
          }
        });

        // Center gravity
        newNodes.forEach(node => {
          const dx = centerX - node.x;
          const dy = centerY - node.y;
          node.vx += dx * 0.001;
          node.vy += dy * 0.001;
        });

        // Apply velocities with damping
        newNodes.forEach(node => {
          node.x += node.vx * 0.3;
          node.y += node.vy * 0.3;
          node.vx *= 0.8;
          node.vy *= 0.8;

          // Keep within bounds
          node.x = Math.max(50, Math.min(width - 50, node.x));
          node.y = Math.max(50, Math.min(height - 50, node.y));
        });

        return newNodes;
      });

      iterations++;
      animationRef.current = requestAnimationFrame(simulate);
    };

    animationRef.current = requestAnimationFrame(simulate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [initialNodes, dependencies]);

  const getConnectedNodes = (nodeId: string): Set<string> => {
    const connected = new Set<string>();
    dependencies.forEach(dep => {
      if (dep.source === nodeId) connected.add(dep.target);
      if (dep.target === nodeId) connected.add(dep.source);
    });
    return connected;
  };

  const connectedNodes = selectedNode ? getConnectedNodes(selectedNode) : new Set<string>();

  return (
    <Card className={cn("p-6", className)}>
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-white">Risk Dependency Map</h3>
        <p className="text-sm text-neutral-400 mt-1">Visualize relationships and cascading effects between risks</p>
      </div>

      <div ref={containerRef} className="relative w-full bg-neutral-950 rounded-lg border border-neutral-800" style={{ height: '400px' }}>
        <svg className="w-full h-full">
          {/* Dependency Lines */}
          <g>
            {dependencies.map((dep, idx) => {
              const source = nodes.find(n => n.id === dep.source);
              const target = nodes.find(n => n.id === dep.target);
              if (!source || !target) return null;

              const isHighlighted = selectedNode === dep.source || selectedNode === dep.target;

              return (
                <line
                  key={`${dep.source}-${dep.target}-${idx}`}
                  x1={source.x}
                  y1={source.y}
                  x2={target.x}
                  y2={target.y}
                  className={cn(
                    getDependencyColor(dep.type),
                    "transition-opacity duration-200",
                    selectedNode && !isHighlighted && "opacity-20"
                  )}
                  strokeWidth={isHighlighted ? 2 : 1}
                  strokeDasharray={dep.type === 'related' ? '4,4' : undefined}
                  markerEnd={`url(#arrow-${dep.type})`}
                />
              );
            })}
          </g>

          {/* Arrow markers */}
          <defs>
            <marker id="arrow-triggers" markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto" markerUnits="strokeWidth">
              <path d="M0,0 L0,6 L9,3 z" fill="rgb(239 68 68)" />
            </marker>
            <marker id="arrow-mitigates" markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto" markerUnits="strokeWidth">
              <path d="M0,0 L0,6 L9,3 z" fill="rgb(34 197 94)" />
            </marker>
            <marker id="arrow-related" markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto" markerUnits="strokeWidth">
              <path d="M0,0 L0,6 L9,3 z" fill="rgb(6 182 212)" />
            </marker>
          </defs>
        </svg>

        {/* Risk Nodes */}
        {nodes.map(node => {
          const isHovered = hoveredNode === node.id;
          const isSelected = selectedNode === node.id;
          const isConnected = connectedNodes.has(node.id);
          const isDimmed = selectedNode && !isSelected && !isConnected;

          return (
            <div
              key={node.id}
              className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer transition-all duration-200"
              style={{ left: `${node.x}px`, top: `${node.y}px` }}
              onMouseEnter={() => setHoveredNode(node.id)}
              onMouseLeave={() => setHoveredNode(null)}
              onClick={() => setSelectedNode(isSelected ? null : node.id)}
            >
              <div
                className={cn(
                  "w-12 h-12 rounded-full border-2 shadow-lg flex items-center justify-center",
                  "transition-all duration-200",
                  getSeverityColor(node.severity),
                  (isHovered || isSelected) && "scale-125 ring-4 ring-white/30",
                  isDimmed && "opacity-30"
                )}
              >
                <span className="text-xs font-bold text-white text-center px-1 leading-tight">
                  {node.name.split(' ')[0]}
                </span>
              </div>

              {/* Tooltip */}
              {isHovered && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-neutral-900 border border-neutral-700 rounded-lg shadow-xl whitespace-nowrap z-10">
                  <p className="text-xs font-semibold text-white">{node.name}</p>
                  <p className="text-xs text-neutral-400 capitalize mt-1">{node.severity} severity</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-6 pt-6 border-t border-neutral-800 space-y-3">
        <div>
          <p className="text-xs font-medium text-neutral-400 mb-2">Dependency Types</p>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <svg width="20" height="2"><line x1="0" y1="1" x2="20" y2="1" className="stroke-red-500" strokeWidth="2" /></svg>
              <span className="text-xs text-neutral-400">Triggers</span>
            </div>
            <div className="flex items-center gap-2">
              <svg width="20" height="2"><line x1="0" y1="1" x2="20" y2="1" className="stroke-green-500" strokeWidth="2" /></svg>
              <span className="text-xs text-neutral-400">Mitigates</span>
            </div>
            <div className="flex items-center gap-2">
              <svg width="20" height="2"><line x1="0" y1="1" x2="20" y2="1" className="stroke-cyan-500" strokeWidth="2" strokeDasharray="4,4" /></svg>
              <span className="text-xs text-neutral-400">Related</span>
            </div>
          </div>
        </div>
        <p className="text-xs text-neutral-500 italic">Click a node to highlight its connections</p>
      </div>
    </Card>
  );
}
